import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { ApiRequestService, FilterPipe, TranslationPipe } from '@prekog/common-ui';
import { Observable } from 'rxjs';
import { ProductBasicInfo } from '../../products/product-basic-info.model';
import { CategoryProduct } from '../../shared/models/category-product.model';
import { Category } from '../../shared/models/category.model';
import { SiteConfiguration } from '../../shared/models/site-configuration.model';
import { LoadSiteConfigurationByKeyUseCase } from '../../use-cases/loadsiteconfigurationbykey/load-site-configuration-by-key.usecase';
import { UpdateSiteConfigurationUseCase } from '../../use-cases/updatesiteconfiguration/update-site-configuration.usecase';
import { CategoryWithProductMappings } from './model/category-with-product-mappings.model';
import { GetCategoryWithProductsResponse } from './model/get-category-with-products.response';
import { UpdateCategoryRequest } from './model/update-category.request';
import { GetCategoryWithProductsUseCase } from './use-cases/get-category-with-products.usecase';

@Component({
    selector: 'app-updatecategory',
    templateUrl: './updatecategory.component.html',
    styleUrls: ['./updatecategory.component.scss'],
    standalone: true,
    imports: [MatIconModule, RouterModule, FormsModule, ReactiveFormsModule, CommonModule, DragDropModule, FilterPipe, MatCheckboxModule, TranslationPipe]
})
export class UpdateCategoryComponent implements OnInit {
    category: Category = {};
    filteredBooks: ProductBasicInfo[] = [];
    offerLinesActive = false;
    offerLinesValue = '';
    productsExcludingCategory: ProductBasicInfo[] = [];
    searchText = '';
    updateableProducts: ProductBasicInfo[] = [];

    private _categoryForm?: FormGroup;
    private _updateCategoryUrl = '/admin/updatecategory';
    private _apiRequestService = inject(ApiRequestService);
    private _fb = inject(FormBuilder);
    private _getCategoryWithProductsUseCase = inject(GetCategoryWithProductsUseCase);
    private _loadSiteConfigUseCase = inject(LoadSiteConfigurationByKeyUseCase);
    private _storeBaseUrl = inject('STORE_BACKEND_URL' as any) as string;
    private _updateSiteConfigUseCase = inject(UpdateSiteConfigurationUseCase);

    get categoryForm(): FormGroup | undefined {
        return this._categoryForm;
    }

    addProductToCategory(product: ProductBasicInfo) {
        const index = this.productsExcludingCategory.indexOf(product);
        this.productsExcludingCategory.splice(index, 1);
        this.updateableProducts.push(product);
    }

    deleteFromCategoryProductList(product: ProductBasicInfo) {
        const index = this.updateableProducts.indexOf(product);
        this.updateableProducts.splice(index, 1);
        this.productsExcludingCategory.push(product);
    }

    ngOnInit(): void {
        this._createCategoryForm();
        const lastSegmentOfUrl = window.location.href.split('/').pop();
        const id = +(lastSegmentOfUrl as string);

        (this._getCategoryWithProductsUseCase.execute({ categoryId: id }) as Observable<GetCategoryWithProductsResponse>)
            .subscribe((response) => {
                this.category = response.category as Category;
                this.updateableProducts = response.categoryProducts as ProductBasicInfo[];
                this.productsExcludingCategory = response.productsExcludingCategory!;
                this.filteredBooks = this.productsExcludingCategory;
                if (sessionStorage.getItem('product')) {
                    const product = JSON.parse(sessionStorage.getItem('product') as string) as ProductBasicInfo;
                    const index = this.productsExcludingCategory.indexOf(product);
                    this.productsExcludingCategory.splice(index, 1);
                    this.updateableProducts.push(product);
                    sessionStorage.removeItem('product');
                }
                this._loadOfferLines();
            });
    }

    private _createCategoryForm(): void {
        this._categoryForm = this._fb.group({
            name: ['', [Validators.required]]
        });
    }

    drop(event: CdkDragDrop<CategoryProduct[]>): void {
        moveItemInArray(this.updateableProducts, event.previousIndex, event.currentIndex);
    }

    updateCategory(): void {
        const categoryProductList: CategoryProduct[] = this.updateableProducts.map((product, index) => {
            const categoryProduct: CategoryProduct = new CategoryProduct();
            categoryProduct.product = product;
            categoryProduct.category = this.category;
            categoryProduct.orderNumber = index;
            return categoryProduct;
        });

        const categoryWithProductMappings: CategoryWithProductMappings = {
            category: this.category,
            categoryProducts: categoryProductList
        };

        const updateCategoryRequest: UpdateCategoryRequest = {
            categoryWithProductMappings: categoryWithProductMappings
        };
        this._apiRequestService
            .sendRequest<UpdateCategoryRequest, any>(
                this._storeBaseUrl,
                updateCategoryRequest,
                true,
                this._updateCategoryUrl,
                true
            )
            .subscribe();
    }

    private _loadOfferLines(): void {
        const key = 'category-' + this.category.id + '-lines';
        (this._loadSiteConfigUseCase.execute({ configurationKey: key }) as Observable<SiteConfiguration>).subscribe((config) => {
            this.offerLinesValue = config.configurationValue ?? '';
            this.offerLinesActive = config.active ?? false;
        });
    }

    updateOfferLines(): void {
        const key = 'category-' + this.category.id + '-lines';
        const result = this._updateSiteConfigUseCase.execute({
            configurationKey: key,
            configurationValue: this.offerLinesValue,
            active: this.offerLinesActive
        });
        if (result) {
            (result as Observable<any>).subscribe(() => { });
        }
    }

    onChangeOfferLinesActive(): void {
        if (!this.offerLinesValue.trim()) {
            this.offerLinesActive = false;
            return;
        }
        this.offerLinesActive = !this.offerLinesActive;
    }
}
